import React, { FC, SyntheticEvent, useEffect } from "react";
// libs
import NextImage from "next/image";
// components
import Button from "src/components/ui/button";
import Checkbox from "src/components/ui/Checkbox";
import Input from "src/components/ui/input/input";
// hooks
import { useRouter } from "next/router";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { useReviews } from "src/hooks/services/useReviews";
// const
import { REG_EXP } from "src/constants/regexp";
// units
import { formatClinicName } from "src/utils/clinics";
// styles
import * as ReasonCheckboxStyles from "src/components/containers/leaveReview/reasonCheckboxes.style";
// types
import { EVariants } from "src/constants/variants";
import {
  BackButtonWrapper,
  BackButtonImageWrapper,
  ContentWrapper,
  SectionsWrapper,
} from "./leaveReview.styles";
import ReviewInputContainer from "./sections/ReviewInput/ReviewInput";
import RateStarsContainer from "./sections/RateStars";
import { amplitudeEvent, formatEventName } from "src/libs/amplitude";
import { Caption, Headline, LargeTitle } from "src/theme/typography";
import { colors } from "src/theme";
import { FlexColumn, FlexRow } from "src/components/ui/common";

interface IReviewForm {
  GoNext: () => void;
}

interface ILeaveReviewForm {
  title: string;
  description: string;
  rating: number;
  other_reasons: string;
  reasons_array: string[];
  reviewer_name: string;
  is_name_hidden: boolean;
  pets_name: string;
  email: string;
  clinic: number; // ID or { id: number }
}

const REASONS = {
  POSITIVE: [
    {
      id: 1,
      label: "Good service",
    },
    {
      id: 2,
      label: "Friendly staff",
    },
    {
      id: 3,
      label: "Quick response",
    },
    {
      id: 4,
      label: "Pet treated well",
    },
    {
      id: 5,
      label: "Other",
    },
  ],
  NEGATIVE: [
    {
      id: 1,
      label: "Bad service",
    },
    {
      id: 2,
      label: "Unfriendly staff",
    },
    {
      id: 3,
      label: "Slow response",
    },
    {
      id: 4,
      label: "Pet not treated well",
    },
    {
      id: 5,
      label: "Other",
    },
  ],
};

const ReviewForm: FC<IReviewForm> = ({ GoNext }) => {
  const router = useRouter();
  const { leaveReview } = useReviews();
  const { clinic = "", id: clinicId } = router.query;

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<ILeaveReviewForm>({
    defaultValues: {
      clinic: Number(clinicId),
    },
  });
  // @ts-ignore
  const { append, remove } = useFieldArray({ control, name: "reasons_array", keyName: "id" });

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  const [ratingWatcher, reasonsArrayWatcher = []] = watch(["rating", "reasons_array"]);

  const handleChangeReason = (e: SyntheticEvent<HTMLInputElement>) => {
    const name = e.currentTarget.name;

    if (reasonsArrayWatcher.includes(name)) {
      remove(reasonsArrayWatcher.findIndex((el) => el === name));
    } else {
      append(name);
    }
  };

  const onSubmit = async (data: ILeaveReviewForm) => {
    amplitudeEvent(formatEventName("leave_review", "form", "submit"));

    try {
      await leaveReview(data);
      GoNext();
    } catch (error) {
      let errorMessage = "Failed to leave review";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      console.log(errorMessage);
    }
  };

  const clinicName = formatClinicName((clinic as string).split("/").slice(-1)[0]);

  const handleGoBack = () => {
    const path = (clinic as string).startsWith("/") ? `/clinics${clinic}` : `/clinics/${clinic}`;
    router.push(path);
  };

  return (
    <ContentWrapper>
      <BackButtonWrapper>
        <BackButtonImageWrapper onClick={handleGoBack}>
          <NextImage src="/icons/arrow-left-black.svg" width={13.5} height={13.5} />
        </BackButtonImageWrapper>
      </BackButtonWrapper>

      <SectionsWrapper>
        <LargeTitle style={{ marginBottom: 40 }}>Leave a review for {clinicName}</LargeTitle>

        <Controller
          name="rating"
          control={control}
          rules={{
            required: "Please add a rating. You can do that by clicking one of the stars.",
          }}
          // Don't set defaultValue, it breaks validation
          // defaultValue={0}
          render={({ field: { value, ...field } }) => (
            <RateStarsContainer
              title={`Rate your experience ${clinicName ? `with ${clinicName}` : ""}`}
              {...field}
              initialRating={value}
              error={errors.rating?.message}
            />
          )}
        />

        <FlexColumn gap="8px">
          <Headline>Review title</Headline>
          <Controller
            name="title"
            control={control}
            rules={{
              required: "Please add a title",
            }}
            defaultValue=""
            render={({ field }) => <Input {...field} errors={errors.title} label="title" />}
          />
          {errors.title && <Caption textColor={colors.redDark}>{errors.title.message}</Caption>}
        </FlexColumn>

        <Controller
          name="description"
          control={control}
          rules={{
            required: "Please add a review",
          }}
          defaultValue=""
          render={({ field }) => (
            <ReviewInputContainer {...field} error={errors.description?.message} />
          )}
        />

        <FlexColumn gap="8px">
          <Headline>What are the main reasons for your score?</Headline>
          <ReasonCheckboxStyles.CheckboxesWrapper>
            {(ratingWatcher <= 2 ? REASONS.NEGATIVE : REASONS.POSITIVE).map(
              ({ id, label }: { id: number; label: string }) => (
                <Checkbox
                  key={id}
                  label={label}
                  name={label}
                  value={reasonsArrayWatcher.includes(label)}
                  onChange={handleChangeReason}
                />
              ),
            )}
          </ReasonCheckboxStyles.CheckboxesWrapper>

          {reasonsArrayWatcher.includes("Other") && (
            <ReasonCheckboxStyles.InputWrapper>
              <Controller
                name="other_reasons"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <ReasonCheckboxStyles.Textarea
                    {...field}
                    disabled={!reasonsArrayWatcher.includes("Other")}
                    rows={5}
                    placeholder="Other reasons"
                  />
                )}
              />
            </ReasonCheckboxStyles.InputWrapper>
          )}
        </FlexColumn>

        <FlexColumn gap="8px">
          <Headline>Your name</Headline>
          <Controller
            name="reviewer_name"
            control={control}
            defaultValue=""
            render={({ field }) => <Input {...field} label="reviewer_name" />}
          />
          <Controller
            name="is_name_hidden"
            control={control}
            defaultValue={false}
            render={({ field }) => <Checkbox {...field} label="Don't show your name" />}
          />
        </FlexColumn>

        <FlexColumn gap="8px">
          <Headline>Pet&apos;s name</Headline>
          <Caption>This won&apos;t appear on the review</Caption>
          <Controller
            name="pets_name"
            control={control}
            defaultValue=""
            render={({ field }) => <Input {...field} label="pets_name" />}
          />
        </FlexColumn>

        <FlexColumn gap="8px">
          <Headline>Email</Headline>
          <Caption>We need your email address to verify your review</Caption>
          <Controller
            name="email"
            control={control}
            rules={{
              required: "Please add an email address",
              pattern: {
                value: REG_EXP.email,
                message: "Entered value does not match email format",
              },
            }}
            defaultValue=""
            render={({ field }) => (
              <Input type="email" {...field} errors={errors.email} label="email" />
            )}
          />
          {errors.email && <Caption textColor={colors.redDark}>{errors.email.message}</Caption>}
        </FlexColumn>

        <FlexRow gap="12px">
          <Button variant={EVariants.PRIMARY} onClick={handleSubmit(onSubmit)}>
            Submit
          </Button>
          {!!Object.keys(errors).length && (
            <Caption textColor={colors.redDark}>
              There are errors in the form above. Please fix them before you submit.
            </Caption>
          )}
        </FlexRow>
      </SectionsWrapper>
    </ContentWrapper>
  );
};

export default ReviewForm;
